/**
 * Task chains (2026-10-13): "When it's approved, the next task is …". Links point at a series
 * (every round of a task is a new row; they share `series_id`, the first round is the series).
 */
type ChainTask = {
  id: string;
  title: string;
  series_id?: string | null;
  next_series_id?: string | null;
  created_at?: Date | string | null;
};

export const seriesOf = (t: ChainTask): string => t.series_id || t.id;

const time = (t: ChainTask) => (t.created_at ? new Date(t.created_at as any).getTime() : 0);

/** The newest round of each series: what a parent thinks of as "the task". */
function latestRounds<T extends ChainTask>(tasks: T[]): T[] {
  const bySeries = new Map<string, T>();
  for (const t of tasks) {
    const cur = bySeries.get(seriesOf(t));
    if (!cur || time(t) > time(cur)) bySeries.set(seriesOf(t), t);
  }
  return Array.from(bySeries.values());
}

export function titleOfSeries(tasks: ChainTask[], series: string | null | undefined): string | null {
  if (!series) return null;
  return latestRounds(tasks.filter((t) => seriesOf(t) === series))[0]?.title ?? null;
}

/** The task that leads to this one (what a waiting step is waiting for). */
export function leaderTitle(tasks: ChainTask[], task: ChainTask): string | null {
  const own = seriesOf(task);
  const leader = latestRounds(tasks).find((t) => t.next_series_id === own && seriesOf(t) !== own);
  return leader?.title ?? null;
}

/** Tasks a parent can pick as "the next task" (one per series, not the task itself), A–Z. */
export function nextTaskChoices(tasks: ChainTask[], ownSeries: string | null): Array<{ series: string; title: string }> {
  return latestRounds(tasks)
    .filter((t) => seriesOf(t) !== ownSeries)
    .map((t) => ({ series: seriesOf(t), title: t.title }))
    .sort((a, b) => a.title.localeCompare(b.title));
}
